import React, { useEffect, useState } from "react";
import {
  GitHub,
  LinkedIn,
  Email,
  Phone,
  LocationOn,
  ArrowUpward,
} from "@mui/icons-material";
import {
  Box,
  Container,
  Grid,
  Typography,
  IconButton,
  Button,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Link } from "react-router-dom";
import {
  contactDetails,
  services,
  socialLinks,
} from "../../config/site";
import { routes } from "../../routes";
import "../../styles/components/Footer.css";

const socialIcons = {
  LinkedIn: <LinkedIn />,
  GitHub: <GitHub />,
  Email: <Email />,
};

function Footer() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.pageYOffset > 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleOpenExternal = (link) => {
    window.open(link, "_blank", "noopener,noreferrer");
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const currentYear = new Date().getFullYear();
  const footerRoutes = routes
    .filter((route) => route.showInFooter !== false)
    .map(({ path, label }) => ({ path, label }));

  return (
    <>
      <footer className="footer-container">
        <Container>
          <div className="footer-section">
            <Grid container spacing={4}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontWeight: 600, mb: 2 }}
                >
                  Cory Smith
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ mb: 2, opacity: 0.9, lineHeight: 1.6 }}
                >
                  Full Stack Developer passionate about creating innovative web
                  solutions and exceptional user experiences.
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  {socialLinks.map((link) => (
                    <IconButton
                      key={link.label}
                      className="social-button"
                      onClick={() => handleOpenExternal(link.href)}
                      size="small"
                      aria-label={link.label}
                    >
                      {socialIcons[link.label]}
                    </IconButton>
                  ))}
                </Box>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontWeight: 600, mb: 2 }}
                >
                  Quick Links
                </Typography>
                {footerRoutes.map((route) => (
                  <Link key={route.path} className="footer-link" to={route.path}>
                    {route.label}
                  </Link>
                ))}
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontWeight: 600, mb: 2 }}
                >
                  Services
                </Typography>
                {services.map((service) => (
                  <Link key={service} className="footer-link" to="/about">
                    {service}
                  </Link>
                ))}
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontWeight: 600, mb: 2 }}
                >
                  Contact Info
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <Email sx={{ fontSize: 16, mr: 1, opacity: 0.8 }} />
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    {contactDetails.email}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <Phone sx={{ fontSize: 16, mr: 1, opacity: 0.8 }} />
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    {contactDetails.phone}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <LocationOn sx={{ fontSize: 16, mr: 1, opacity: 0.8 }} />
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    {contactDetails.location}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3, borderColor: "rgba(255,255,255,0.2)" }} />

            <Box
              sx={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                © {currentYear} Cory Evan Smith. All rights reserved.
              </Typography>

              <Box sx={{ display: "flex", gap: 2 }}>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Built with React & Material-UI
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  • Deployed on Netlify
                </Typography>
              </Box>
            </Box>
          </div>
        </Container>
      </footer>

      <Button
        className={`scroll-to-top-button ${!showScrollTop ? "hidden" : ""}`}
        onClick={scrollToTop}
        aria-label="Scroll to top"
        sx={{
          position: "fixed",
          bottom: { xs: "0.75rem", sm: "2rem" },
          right: { xs: "0.75rem", sm: "2rem" },
          height: { xs: 44, sm: 56 },
          minWidth: { xs: 44, sm: 56 },
          width: { xs: 44, sm: 56 },
          zIndex: 1000,
        }}
      >
        <ArrowUpward />
      </Button>
    </>
  );
}

export default Footer;
